import dynamic, {
	type DynamicOptions,
	type DynamicOptionsLoadingProps,
} from 'next/dynamic';
import { type ComponentType, type ReactNode, Suspense } from 'react';

// Type definitions
type ComponentWithProps<P extends object = object> = ComponentType<P>;
type DynamicImportFn<P extends object> = () => Promise<
	| {
			default: ComponentWithProps<P>;
	  }
	| {
			[key: string]: ComponentWithProps<P>;
	  }
>;

interface ErrorBoundaryProps {
	error: Error;
	reset: () => void;
}

interface DynamicConfig<P extends object>
	extends Omit<DynamicOptions<P>, 'loading'> {
	/**
	 * Whether to wrap with Suspense boundary
	 * @default false
	 */
	withSuspense?: boolean;
	/**
	 * Custom loading component
	 * @default () => <div>Loading...</div>
	 */
	LoadingComponent?: (props: DynamicOptionsLoadingProps) => ReactNode;
	/**
	 * Custom error component
	 * @default () => <div>Error</div>
	 */
	ErrorComponent?: ComponentType<ErrorBoundaryProps>;
}

const DefaultError: ComponentType<ErrorBoundaryProps> = ({ error }) => (
	<div>Error: {error.message}</div>
);

/**
 * Creates a dynamic component with proper typing and error handling
 */
export function createDynamic<P extends object>(
	importFn: DynamicImportFn<P>,
	{
		withSuspense = false,
		LoadingComponent = () => (
			<div className="flex h-full w-full items-center justify-center">
				Loading...
			</div>
		),
		ErrorComponent = DefaultError,
		...options
	}: DynamicConfig<P> = {},
): ComponentType<P> {
	const DynamicComponent = dynamic<P>(
		() =>
			importFn().then((mod) => {
				const Component =
					'default' in mod ? mod.default : Object.values(mod)[0];
				return Component as ComponentType<P>;
			}),
		{
			loading: LoadingComponent,
			ssr: true,
			...options,
		},
	);

	if (!withSuspense) {
		return DynamicComponent;
	}

	return function DynamicWithSuspense(props: P) {
		return (
			<Suspense fallback={<LoadingComponent />}>
				<DynamicComponent {...props} />
			</Suspense>
		);
	};
}

/**
 * Creates a page component with default suspense and error handling
 */
export function createPage<P extends object>(
	importFn: DynamicImportFn<P>,
	options: Omit<DynamicConfig<P>, 'withSuspense'> = {},
): ComponentType<P> {
	return createDynamic<P>(importFn, {
		withSuspense: true,
		...options,
	});
}
